import { useState } from "react";

const InterviewSelectForm = ({ onSubmit, onCancel, loading = false }) => {
  const [formData, setFormData] = useState({
    decision_notes: "",
    feedback_categories: {
      technical: "",
      communication: "",
      problem_solving: "",
    },
    evaluation_weights: {
      technical: "",
      communication: "",
      problem_solving: "",
    },
  });

  const [errors, setErrors] = useState({});

  const feedbackOptions = ["Excellent", "Very Good", "Good", "Average", "Poor"];

  const handleChange = (field, value) => {
    if (field.startsWith("feedback_categories.")) {
      const categoryKey = field.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        feedback_categories: {
          ...prev.feedback_categories,
          [categoryKey]: value,
        },
      }));
      // Clear error for this field
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    } else if (field.startsWith("evaluation_weights.")) {
      const weightKey = field.split(".")[1];
      // Only allow numbers and decimal point for evaluation weights
      const numericValue = value.replace(/[^\d.]/g, '');
      // Limit to 5 digits (not counting decimal point)
      const digitsOnly = numericValue.replace(/\./g, '');
      if (digitsOnly.length > 5) {
        return; // Don't update if more than 5 digits
      }
      setFormData((prev) => ({
        ...prev,
        evaluation_weights: {
          ...prev.evaluation_weights,
          [weightKey]: numericValue,
        },
      }));
      // Clear error for this field
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
      // Clear digit count error when weights change
      if (errors[`${field}_digits`]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[`${field}_digits`];
          return newErrors;
        });
      }
      // Clear sum error when weights change
      if (errors.weightSum) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.weightSum;
          return newErrors;
        });
      }
    } else {
      // For decision_notes, check character limit
      if (field === "decision_notes" && value.length > 500) {
        setErrors((prev) => ({
          ...prev,
          decision_notes: "Decision notes cannot exceed 500 characters",
        }));
        return; // Don't update if exceeds limit
      }
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
      // Clear error for this field
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const validate = () => {
    const newErrors = {};

    // Validate decision_notes
    if (!formData.decision_notes.trim()) {
      newErrors.decision_notes = "Decision notes are required";
    } else if (formData.decision_notes.length > 500) {
      newErrors.decision_notes = "Decision notes cannot exceed 500 characters";
    }

    // Validate feedback_categories
    if (!formData.feedback_categories.technical) {
      newErrors["feedback_categories.technical"] = "Technical feedback is required";
    }
    if (!formData.feedback_categories.communication) {
      newErrors["feedback_categories.communication"] = "Communication feedback is required";
    }
    if (!formData.feedback_categories.problem_solving) {
      newErrors["feedback_categories.problem_solving"] = "Problem solving feedback is required";
    }

    // Validate evaluation_weights
    // Check digit count first
    const technicalDigits = formData.evaluation_weights.technical.replace(/\./g, '').length;
    const communicationDigits = formData.evaluation_weights.communication.replace(/\./g, '').length;
    const problemSolvingDigits = formData.evaluation_weights.problem_solving.replace(/\./g, '').length;

    if (technicalDigits > 5) {
      newErrors["evaluation_weights.technical_digits"] = "Technical weight cannot exceed 5 digits";
    }
    if (communicationDigits > 5) {
      newErrors["evaluation_weights.communication_digits"] = "Communication weight cannot exceed 5 digits";
    }
    if (problemSolvingDigits > 5) {
      newErrors["evaluation_weights.problem_solving_digits"] = "Problem solving weight cannot exceed 5 digits";
    }

    const technicalWeight = parseFloat(formData.evaluation_weights.technical) || 0;
    const communicationWeight = parseFloat(formData.evaluation_weights.communication) || 0;
    const problemSolvingWeight = parseFloat(formData.evaluation_weights.problem_solving) || 0;

    if (!formData.evaluation_weights.technical && formData.evaluation_weights.technical !== 0) {
      newErrors["evaluation_weights.technical"] = "Technical weight is required";
    } else if (technicalWeight < 0 || technicalWeight > 1) {
      newErrors["evaluation_weights.technical"] = "Weight must be between 0 and 1";
    }

    if (!formData.evaluation_weights.communication && formData.evaluation_weights.communication !== 0) {
      newErrors["evaluation_weights.communication"] = "Communication weight is required";
    } else if (communicationWeight < 0 || communicationWeight > 1) {
      newErrors["evaluation_weights.communication"] = "Weight must be between 0 and 1";
    }

    if (!formData.evaluation_weights.problem_solving && formData.evaluation_weights.problem_solving !== 0) {
      newErrors["evaluation_weights.problem_solving"] = "Problem solving weight is required";
    } else if (problemSolvingWeight < 0 || problemSolvingWeight > 1) {
      newErrors["evaluation_weights.problem_solving"] = "Weight must be between 0 and 1";
    }

    // Validate weights sum
    const weightSum = technicalWeight + communicationWeight + problemSolvingWeight;
    if (weightSum < 0.99 || weightSum > 1.01) {
      newErrors.weightSum = "Evaluation weights must sum to 1.0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    const submissionData = {
      status: "complete",
      decision: "Selected",
      decision_notes: formData.decision_notes.trim(),
      feedback_categories: {
        technical: formData.feedback_categories.technical,
        communication: formData.feedback_categories.communication,
        problem_solving: formData.feedback_categories.problem_solving,
      },
      evaluation_weights: {
        technical: parseFloat(formData.evaluation_weights.technical),
        communication: parseFloat(formData.evaluation_weights.communication),
        problem_solving: parseFloat(formData.evaluation_weights.problem_solving),
      },
    };

    onSubmit(submissionData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Decision Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Decision Notes <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.decision_notes}
          onChange={(e) => handleChange("decision_notes", e.target.value)}
          rows={4}
          maxLength={500}
          className={`w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.decision_notes ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="Candidate demonstrated excellent technical skills and strong communication abilities. Recommended for next round."
        />
        <div className="flex justify-between items-center mt-1">
          {errors.decision_notes && (
            <p className="text-sm text-red-500">{errors.decision_notes}</p>
          )}
          <p className={`text-sm ml-auto text-[10px] ${
            formData.decision_notes.length > 500 ? "text-red-500" : "text-gray-500 dark:text-gray-400"
          }`}>
            {formData.decision_notes.length}/500
          </p>
        </div>
      </div>

      {/* Feedback Categories */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Feedback Categories</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Technical <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.feedback_categories.technical}
            onChange={(e) => handleChange("feedback_categories.technical", e.target.value)}
            className={`w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors["feedback_categories.technical"] ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Select rating</option>
            {feedbackOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          {errors["feedback_categories.technical"] && (
            <p className="mt-1 text-sm text-red-500">{errors["feedback_categories.technical"]}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Communication <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.feedback_categories.communication}
            onChange={(e) => handleChange("feedback_categories.communication", e.target.value)}
            className={`w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors["feedback_categories.communication"] ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Select rating</option>
            {feedbackOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          {errors["feedback_categories.communication"] && (
            <p className="mt-1 text-sm text-red-500">{errors["feedback_categories.communication"]}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Problem Solving <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.feedback_categories.problem_solving}
            onChange={(e) => handleChange("feedback_categories.problem_solving", e.target.value)}
            className={`w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors["feedback_categories.problem_solving"] ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Select rating</option>
            {feedbackOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          {errors["feedback_categories.problem_solving"] && (
            <p className="mt-1 text-sm text-red-500">{errors["feedback_categories.problem_solving"]}</p>
          )}
        </div>
      </div>

      {/* Evaluation Weights */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Evaluation Weights</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Weights must sum to 1.0 (e.g., 0.4, 0.3, 0.3)
        </p>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Technical Weight <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.evaluation_weights.technical}
            onChange={(e) => handleChange("evaluation_weights.technical", e.target.value)}
            className={`w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors["evaluation_weights.technical"] || errors["evaluation_weights.technical_digits"] ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="0.4"
          />
          {errors["evaluation_weights.technical"] && (
            <p className="mt-1 text-sm text-red-500">{errors["evaluation_weights.technical"]}</p>
          )}
          {errors["evaluation_weights.technical_digits"] && (
            <p className="mt-1 text-sm text-red-500">{errors["evaluation_weights.technical_digits"]}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Communication Weight <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.evaluation_weights.communication}
            onChange={(e) => handleChange("evaluation_weights.communication", e.target.value)}
            className={`w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors["evaluation_weights.communication"] || errors["evaluation_weights.communication_digits"] ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="0.3"
          />
          {errors["evaluation_weights.communication"] && (
            <p className="mt-1 text-sm text-red-500">{errors["evaluation_weights.communication"]}</p>
          )}
          {errors["evaluation_weights.communication_digits"] && (
            <p className="mt-1 text-sm text-red-500">{errors["evaluation_weights.communication_digits"]}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Problem Solving Weight <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.evaluation_weights.problem_solving}
            onChange={(e) => handleChange("evaluation_weights.problem_solving", e.target.value)}
            className={`w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors["evaluation_weights.problem_solving"] || errors["evaluation_weights.problem_solving_digits"] ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="0.3"
          />
          {errors["evaluation_weights.problem_solving"] && (
            <p className="mt-1 text-sm text-red-500">{errors["evaluation_weights.problem_solving"]}</p>
          )}
          {errors["evaluation_weights.problem_solving_digits"] && (
            <p className="mt-1 text-sm text-red-500">{errors["evaluation_weights.problem_solving_digits"]}</p>
          )}
        </div>

        {errors.weightSum && (
          <p className="text-sm text-red-500">{errors.weightSum}</p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Submitting..." : "Submit Selection"}
        </button>
      </div>
    </form>
  );
};

export default InterviewSelectForm;

